import React, { useState } from "react";
import "./App.css";
// import MaintenancePage from "./components/MaintenancePage";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import WhatsAppFAB from "./components/WhatsAppFAB";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./components/AuthCallback";
import ScrollToTop from "./components/ScrollToTop";
import BootLoader from "./components/BootLoader";

import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import SalePage from "./pages/SalePage";
import ProductDetailPage from "./pages/ProductDetailPage";
import SearchResultsPage from "./pages/SearchResultsPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import PreOrderCheckoutPage from "./pages/PreOrderCheckoutPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import PreOrderSuccessPage from "./pages/PreOrderSuccessPage";
import RepairsPage from "./pages/RepairsPage";
import RepairTrackingPage from "./pages/RepairTrackingPage";
import TradeInPage from "./pages/TradeInPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import PolicyPage from "./pages/PolicyPage";
import TrackOrderPage from "./pages/TrackOrderPage";
import InventoryPage from "./pages/InventoryPage";
import CommunityPage from "./pages/CommunityPage";
import PostDetailPage from "./pages/PostDetailPage";

import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminSummaryPage from "./pages/admin/AdminSummary";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminPosts from "./pages/admin/AdminPosts";
import AdminRepairs from "./pages/admin/AdminRepairs";
import AdminTradeIn from "./pages/admin/AdminTradeIn";
import AdminPreorders from "./pages/admin/AdminPreorders";
import AdminCoupons from "./pages/admin/AdminCoupons";
import AdminSales from "./pages/admin/AdminSales";
import AdminBilling from "./pages/admin/AdminBilling";
import AdminBackup from "./pages/admin/AdminBackup";
import AdminMissingImages from "./pages/admin/AdminMissingImages";

function PublicLayout({ children }) {
  return (
    <>
      <Header />
      <main className="min-h-screen">{children}</main>
      <Footer />
      <WhatsAppFAB />
    </>
  );
}

function AppRouter({ homeData }) {
  const location = useLocation();
  if (location.hash?.includes("email=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
          <Route index element={<AdminSummaryPage />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="preorders" element={<AdminPreorders />} />
          <Route path="repairs" element={<AdminRepairs />} />
          <Route path="tradein" element={<AdminTradeIn />} />
          <Route path="coupons" element={<AdminCoupons />} />
          <Route path="sales" element={<AdminSales />} />
          <Route path="posts" element={<AdminPosts />} />
          <Route path="billing" element={<AdminBilling />} />
          <Route path="backup" element={<AdminBackup />} />
          <Route path="missing-images" element={<AdminMissingImages />} />
        </Route>
        <Route path="*" element={
          <PublicLayout>
            <Routes>
              <Route path="/" element={<HomePage preloaded={homeData} />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/inventory" element={<InventoryPage />} />
              <Route path="/sale/:slug" element={<SalePage />} />
              <Route path="/search" element={<SearchResultsPage />} />
              <Route path="/product/:id" element={<ProductDetailPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/preorder-checkout" element={<PreOrderCheckoutPage />} />
              <Route path="/order/:id" element={<OrderSuccessPage />} />
              <Route path="/preorder-success/:id" element={<PreOrderSuccessPage />} />
              <Route path="/track" element={<TrackOrderPage />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/community/:id" element={<PostDetailPage />} />
              <Route path="/repairs" element={<RepairsPage />} />
              <Route path="/repair-track" element={<RepairTrackingPage />} />
              <Route path="/trade-in" element={<TradeInPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/policy/:slug" element={<PolicyPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </PublicLayout>
        } />
      </Routes>
    </>
  );
}

// const _previewParam = new URLSearchParams(window.location.search);
// if (_previewParam.get("preview") === "true") {
//   // Set a long-lived cookie so this browser always bypasses maintenance
//   document.cookie = "gt_preview=1; path=/; max-age=31536000; SameSite=Lax";
// }
// const SHOW_MAINTENANCE = !document.cookie.split(";").some(c => c.trim().startsWith("gt_preview=1"));

function App() {
  // Show boot loader only once per browser session, and never on deep-links other than "/"
  const isHome = typeof window !== "undefined" && window.location.pathname === "/";
  const alreadyBooted = typeof window !== "undefined" && sessionStorage.getItem("gt_booted") === "1";
  const [booted, setBooted] = useState(!isHome || alreadyBooted);
  const [homeData, setHomeData] = useState(null);

  // if (SHOW_MAINTENANCE) return <MaintenancePage />;

  return (
    <HelmetProvider>
      <div className="App">
        {!booted && (
          <BootLoader
            onReady={() => {
              try { sessionStorage.setItem("gt_booted", "1"); } catch {}
              setBooted(true);
            }}
            preloadData={setHomeData}
          />
        )}
        <BrowserRouter>
          <AuthProvider>
            <CartProvider>
              <AppRouter homeData={homeData} />
              <Toaster theme="dark" position="bottom-right" richColors duration={1500} closeButton />
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
